import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CustomerTag, DeleteCustomerTagResponse, CustomerTagCategory } from "./customerTagsApi";
import { addCustomerTag, deleteCustomerTag, listCustomerTags, updateCustomerTag } from "./customerTagsApi";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";

const customerTagsKey = "customer-tags-dictionary";

export const useCustomerTags = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [customerTagsKey],
    queryFn: ({ signal }) => listCustomerTags(signal),
  });

  const addMutation = useMutation<CustomerTag, unknown, { category: CustomerTagCategory; name: string }>({
    mutationFn: ({ category, name }) => addCustomerTag(category, name),
    onSuccess: async (data) => {
      enqueueSnackbar(`Tag '${data.name}' w kategorii '${data.category}' został dodany do słownika`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [customerTagsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać taga do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateMutation = useMutation<
    CustomerTag,
    unknown,
    { readonly id: string; readonly category: CustomerTagCategory; readonly name: string }
  >({
    mutationFn: ({ id, category, name }) => updateCustomerTag(id, category, name),
    onSuccess: async ({ name }) => {
      enqueueSnackbar(`Tag został zaktualizowany: ${name}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [customerTagsKey] });
      await queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.at(0) === "customers" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować taga. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<DeleteCustomerTagResponse, unknown, string>({
    mutationFn: (id) => deleteCustomerTag(id),
    onSuccess: async ({ updatedCustomers }) => {
      enqueueSnackbar(`Tag został usunięty ze słownika. Zaktualizowano ${updatedCustomers} klientów`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({ queryKey: [customerTagsKey] });
      await queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.at(0) === "customers" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć taga ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    tags: sortBy(
      data ?? [],
      ({ category }) => category.trim().toLowerCase(),
      ({ name }) => name.trim().toLowerCase(),
    ),
    isLoading,
    addTag: (category: CustomerTagCategory, name: string) => addMutation.mutateAsync({ category, name }),
    updateTag: (id: string, category: CustomerTagCategory, name: string) =>
      updateMutation.mutateAsync({ id, category, name }),
    deleteTag: (id: string) => deleteMutation.mutateAsync(id),
  };
};
